import EntryLanguageFooter from "../../EntryLanguageFooter";
import React, { useState, useMemo, useEffect } from "react";
import { useUser } from "../../../hooks/useApi";
import Loader from "../../basic/Loader";
import Button from "../../basic/Button";
import InputText from "../../basic/InputText";
import DevPre from "../../dev/DevPre";
import "./styles/style.scss";
import EmailLoginDiagnosticsPanel from "../../EmailLoginDiagnosticsPanel";

import useLanguage, { T } from "../../../hooks/useLanguage";
import {
  normalizeEmailForLogin,
  normalizeVerificationCode,
} from "../../../services/emailLoginDiagnostics";

const EMAIL_REGEX = new RegExp(
  // eslint-disable-next-line no-useless-escape
  "^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+.[a-zA-Z0-9-.]+$"
);

const EmailVerification = function () {
  const {
    mutation,
    verifyEmail,
    verifyEmailMutation,
    notificationid,
    confirmloginMutation,
    verifyEmailStatusQuery,
    emailLoginCheckFailure,
  } = useUser();
  const { isLoading, isError, error } = mutation;

  const [email, setEmail] = useState("");
  const [sentEmail, setSentEmail] = useState(email);

  const { i18n } = useLanguage();

  const emailIsValid = useMemo(() => EMAIL_REGEX.test(email), [email]);
  const [verificationCode, setVerificationCode] = useState("");

  const handleVerify = () => {
    const normalizedEmail = normalizeEmailForLogin(email);
    setEmail(normalizedEmail);
    verifyEmail(normalizedEmail);
    setSentEmail(normalizedEmail);
  };

  const handleConfirmLogin = () => {
    confirmloginMutation.mutate(verificationCode);
  };

  useEffect(() => {
    if (sentEmail !== email) {
      if (verifyEmailMutation.status !== "idle") {
        verifyEmailMutation.reset();
      }
      setSentEmail("");
    }
  }, [email, sentEmail, verifyEmailMutation]);

  useEffect(() => {
    return () => confirmloginMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isLoadingAny = verifyEmailMutation.isLoading || isLoading || verifyEmailStatusQuery.isLoading;

  return (
    <div className="container verify">
      <h1>
        <T>verify.title</T>
      </h1>
      {verifyEmailMutation.status === "idle" && (
        <p className="verify__message">
          <T>verify.instructions</T>
        </p>
      )}
      <InputText
        label={<T>login.email</T>}
        type="text"
        value={email}
        name="username"
        onChange={setEmail}
        placeholder={i18n.t("verify.notvalidemailmessage")}
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
      />
      {email !== "" && !emailIsValid && <T>verify.notvalidemailmessage</T>}
      <Button
        onClick={handleVerify}
        block
        className="success"
        disabled={!emailIsValid}
      >
        {verifyEmailMutation?.isLoading ? (
          <Loader />
        ) : (
          <T>
            {verifyEmailMutation.isSuccess
              ? "verify.sendagain"
              : "verify.verify"}
          </T>
        )}
      </Button>
      {verifyEmailMutation.status === "success" && (
        <>
          <p className="verify__message">
            <T email={email}>verify.description</T>
          </p>
          <input
            type="text"
            value={verificationCode}
            onChange={(e) => setVerificationCode(normalizeVerificationCode(e.target.value))}
            aria-label={i18n.t("verify.code")}
            placeholder={i18n.t("verify.code")}
            autoComplete="one-time-code"
            autoCorrect="off"
            spellCheck={false}
          />
          <Button
            onClick={handleConfirmLogin}
            className="success"
            loading={confirmloginMutation.isLoading}
            disabled={!(normalizeVerificationCode(verificationCode)?.length > 3) || isLoadingAny}
          >
            {confirmloginMutation.isLoading ? <Loader /> : <T>verify.verify</T>}
          </Button>
          {confirmloginMutation.isError && (
            <EmailLoginDiagnosticsPanel error={confirmloginMutation.error} />
          )}
          {emailLoginCheckFailure && (
            <EmailLoginDiagnosticsPanel
              error={{
                message: emailLoginCheckFailure.message,
                response: {
                  data: {
                    msg: emailLoginCheckFailure.message,
                    reason: "login_not_found",
                  },
                },
              }}
              titleKey="verify.loginnotfoundtitle"
              hintKey="verify.loginnotfoundhint"
            />
          )}
          {!emailLoginCheckFailure && (
            <p className="verify__message" style={{ textAlign: "center" }}>
              <T>verify.waiting</T>
            </p>
          )}
        </>
      )}
      {verifyEmailMutation.isError && (
        <div className="error danger">
          {verifyEmailMutation.error?.message && (
            <>
              <small>{verifyEmailMutation.error.message}</small>
            </>
          )}
          <p>
            <T>verify.sendemailerror</T>
          </p>
        </div>
      )}
      <DevPre>
        <div>{verifyEmailMutation.status}</div>
        <div>{`email: "${email}"`}</div>
        <div>{`sentEmail: "${sentEmail}"`}</div>
        <div>{`notificationid: "${notificationid}"`}</div>
        <div>{`verificationCodeLength: "${normalizeVerificationCode(verificationCode).length}"`}</div>
      </DevPre>
      {isError ? (
        <p style={{ color: "red" }}>
          {error?.message ? error.message : "Unknown error"}
        </p>
      ) : isLoadingAny ? (
        <>
          <Loader />
        </>
      ) : null}
      <EntryLanguageFooter />
    </div>
  );
};
export default EmailVerification;
